import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareMore, Loader2, CheckCircle2, Clock, Circle, Trash2, AlertCircle, Eye, MoreHorizontal, X, Trash } from 'lucide-react';
import type { Site } from '../types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getFeedbackTickets,
  getFeedbackToken,
  activateFeedback,
  updateTicketStatus,
  deleteFeedbackTicket,
  type FeedbackTicket,
  type FeedbackToken,
} from '@/lib/feedbackService';
import { FeedbackDetailPanel } from './FeedbackDetailPanel';
import { toast } from '@/hooks/use-toast';

interface FeedbackTabProps {
  site: Site;
  searchTerm: string;
  refreshTrigger: boolean;
}

const STATUS_TABS = [
  { id: 'all',         label: 'All' },
  { id: 'open',        label: 'Open' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'review',      label: 'Review' },
  { id: 'resolved',    label: 'Resolved' },
];

function StatusBadge({ status }: { status: string }) {
  const config = {
    open:        { icon: Circle,       label: 'Open',        cls: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/40' },
    in_progress: { icon: Clock,        label: 'In Progress', cls: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/40' },
    review:      { icon: Eye,          label: 'Review',      cls: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/40' },
    resolved:    { icon: CheckCircle2, label: 'Resolved',    cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/40' },
  }[status] ?? { icon: Circle, label: status, cls: 'text-muted-foreground bg-muted border-border' };

  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${config.cls}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function PriorityBadge({ priority = 'low' }: { priority?: string }) {
  const normalized = priority.toLowerCase();
  const config = {
    critical: { label: 'Critical', cls: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/40' },
    high:     { label: 'High',     cls: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/40' },
    medium:   { label: 'Medium',   cls: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/40' },
    low:      { label: 'Low',      cls: 'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800/40' },
  }[normalized] ?? { label: priority, cls: 'text-muted-foreground bg-muted border-border' };

  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${config.cls}`}>
      {config.label}
    </span>
  );
}

export function FeedbackTab({ site, searchTerm, refreshTrigger }: FeedbackTabProps) {
  const [tickets, setTickets]             = useState<FeedbackTicket[]>([]);
  const [token, setToken]                 = useState<FeedbackToken | null>(null);
  const [isLoading, setIsLoading]         = useState(true);
  const [isActivating, setIsActivating]   = useState(false);
  const [statusFilter, setStatusFilter]   = useState('all');
  const [selectedTicket, setSelectedTicket] = useState<FeedbackTicket | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [tkn, tix] = await Promise.all([
        getFeedbackToken(site.id),
        getFeedbackTickets(site.id),
      ]);
      setToken(tkn);
      setTickets(tix);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [site.id]);

  useEffect(() => { load(); }, [site.id, refreshTrigger, load]);

  const handleActivate = async () => {
    setIsActivating(true);
    try {
      const tkn = await activateFeedback(site);
      setToken(tkn);
      toast({ title: 'Feedback Widget Enabled', description: 'The widget is now live on your site.' });
    } catch (err: any) {
      toast({ title: 'Activation Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsActivating(false);
    }
  };


  const handleStatusChange = async (id: string, newStatus: FeedbackTicket['status'], newPriority?: FeedbackTicket['priority']) => {
    try {
      await updateTicketStatus(id, newStatus, newPriority);
      setTickets(prev =>
        prev.map(t => (t.id === id ? { ...t, status: newStatus, ...(newPriority ? { priority: newPriority } : {}) } : t))
      );
      setSelectedTicket(prev => prev?.id === id ? { ...prev, status: newStatus, ...(newPriority ? { priority: newPriority } : {}) } : prev);
      toast({ title: 'Ticket updated' });
    } catch (error) {
      toast({ title: 'Error updating ticket', variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteFeedbackTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
      if (selectedTicket?.id === id) setSelectedTicket(null);
      toast({ title: 'Ticket Deleted' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Filtered list
  const filtered = tickets.filter(t => {
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const low = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      t.page_url.toLowerCase().includes(low) ||
      t.comment.toLowerCase().includes(low) ||
      t.author_name.toLowerCase().includes(low);
    return matchesStatus && matchesSearch;
  });

  const counts = {
    open:        tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    review:      tickets.filter(t => t.status === 'review').length,
    resolved:    tickets.filter(t => t.status === 'resolved').length,
  };

  const isWidgetActive = token?.is_active === true;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Widget activation banner */}
      {!isWidgetActive && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border bg-card border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted/50">
              <MessageSquareMore className="w-4 h-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                Feedback Widget is Disabled
              </p>
              <p className="text-xs text-muted-foreground">
                Enable the widget so visitors can pin feedback directly on the site
              </p>
            </div>
          </div>
          <button
            onClick={handleActivate}
            disabled={isActivating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60 flex-shrink-0 bg-workspace-accent text-white hover:bg-workspace-accent-hover shadow-sm"
          >
            {isActivating && <Loader2 className="w-4 h-4 animate-spin" />}
            Enable Widget
          </button>
        </div>
      )}

      {/* Stats row */}
      {tickets.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Open',        count: counts.open,        cls: 'text-blue-600 dark:text-blue-400' },
            { label: 'In Progress', count: counts.in_progress, cls: 'text-amber-600 dark:text-amber-400' },
            { label: 'Review',      count: counts.review,      cls: 'text-violet-600 dark:text-violet-400' },
            { label: 'Resolved',    count: counts.resolved,    cls: 'text-emerald-600 dark:text-emerald-400' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl px-4 py-3 text-center">
              <p className={`text-2xl font-black ${s.cls}`}>{s.count}</p>
              <p className="text-xs text-muted-foreground font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Status filter tabs */}
      {tickets.length > 0 && (
        <div className="flex p-1 bg-muted/50 rounded-xl w-fit">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                statusFilter === tab.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Ticket list */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Page</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Comment</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Author</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
                      <div className="w-16 h-16 rounded-3xl bg-muted/30 flex items-center justify-center">
                        {isWidgetActive
                          ? <MessageSquareMore className="w-8 h-8 text-muted-foreground/50" />
                          : <AlertCircle className="w-8 h-8 text-muted-foreground/50" />
                        }
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-foreground">
                          {isWidgetActive ? 'No feedback yet' : 'Widget is disabled'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isWidgetActive
                            ? 'Feedback tickets will appear here once visitors start using the widget.'
                            : 'Enable the feedback widget above to start collecting visual feedback.'
                          }
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(ticket => (
                  <motion.tr
                    key={ticket.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="group hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                      <PriorityBadge priority={ticket.priority} />
                    </td>
                    <td className="px-6 py-4 max-w-[160px]">
                      <span className="text-xs text-muted-foreground truncate block" title={ticket.page_url}>
                        {ticket.page_url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-[220px]">
                      <p className="text-sm text-foreground truncate">{ticket.comment}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-foreground">{ticket.author_name}</p>
                      {ticket.author_email && (
                        <p className="text-xs text-muted-foreground">{ticket.author_email}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-muted-foreground">
                        {new Date(ticket.created_at).toLocaleDateString([], { dateStyle: 'medium' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <select
                          value={ticket.status}
                          onChange={e => handleStatusChange(ticket.id, e.target.value as FeedbackTicket['status'], ticket.priority)}
                          className="text-xs bg-background border border-border rounded-lg px-2 py-1.5 text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="review">Review</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              title="More actions"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem 
                              destructive 
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this ticket?')) {
                                  handleDelete(ticket.id);
                                }
                              }}
                              className="cursor-pointer gap-2"
                            >
                              <Trash className="w-4 h-4" />
                              <span>Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      <AnimatePresence>
        {selectedTicket && (
          <FeedbackDetailPanel
            ticket={selectedTicket}
            site={site}
            onClose={() => setSelectedTicket(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
